import React from 'react';
import { X } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import type { TaskItem } from '../../../../types/topic';
import { extractBaseColor, getStatusColor } from '../../../../utils/colorUtils';

interface InlineColumnSelectorProps {
  columns: readonly {
    id: string;
    label: string;
    color?: string;
  }[];
  activeColumn: TaskItem['status'];
  onColumnSelect: (columnId: TaskItem['status']) => void;
  onClose: () => void;
}

export function InlineColumnSelector({
  columns,
  onColumnSelect,
  onClose,
}: InlineColumnSelectorProps) {
  // Format columns for color utils
  const columnsForColorUtil = columns.map(col => {
    const baseColor = extractBaseColor(col.color);
    const colorName = baseColor || 'gray';
    return {
      status: col.id,
      color: colorName,
    };
  });

  // Handle column selection
  const handleSelect = (columnId: string) => {
    onColumnSelect(columnId);
    onClose();
  };

  // Get background color for a column
  const getBgColor = (columnId: string) => {
    const baseColor = getStatusColor(columnId, columnsForColorUtil, 'bg')
      .replace('bg-', '')
      .replace('-100', '-500');

    return baseColor;
  };

  return (
    <div className="w-full h-full flex items-center px-4">
      {/* Close button */}
      <button
        onClick={onClose}
        className="flex-none w-9 h-9 flex items-center justify-center rounded-lg bg-gray-200 text-gray-700 mr-2"
      >
        <X size={16} />
      </button>

      {/* Column options */}
      <div className="flex-1 overflow-x-auto py-1.5">
        <div className="flex gap-2 items-center">
          {columns.map(column => (
            <button
              key={column.id}
              onClick={() => handleSelect(column.id)}
              className={cn(
                'flex-none h-9 px-4 rounded-lg text-sm font-medium',
                'flex items-center justify-center whitespace-nowrap transition-all duration-150',
                'text-white',
                `bg-${getBgColor(column.id)}`
              )}
              data-column-id={column.id}
            >
              {column.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
